/**
 * Switcher de gerencia activa (multi-gerencia F1).
 *
 * Se monta en el Header. Muestra dropdown con las gerencias a los que el usuario
 * logueado tiene acceso:
 *   - `ADMIN_PMIS` por short-circuit ve TODOS las gerencias del catálogo.
 *   - Otros roles ven solo los códigos que están en `usuario.gerenciasAccesibles`.
 *   - Si el usuario no tiene `gerenciasAccesibles` definido (datos legacy), ve solo "CO".
 *
 * Cambiar de gerencia NO requiere relogueo — el evento se dispara y todas las
 * queries que filtran por gerencia (F2) se re-evalúan automáticamente.
 */

import { useMemo } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
} from "@fluentui/react-components";
import { ChevronDownRegular } from "@fluentui/react-icons";
import { useAuth } from "@core/auth/useAuth";
import { useUsuariosActivos } from "@features/autoridades/autoridadesQueries";
import { infoGerencia, type GerenciaCodigo } from "@constants/gerencias";
import {
  useGerenciaActiva,
  establecerGerenciaActiva,
  leerGerenciaActiva,
  useVerTodasLasGerencias,
  establecerVerTodasLasGerencias,
  useGerenciasCatalogo,
} from "@core/gerenciaActiva";
import { useEffect } from "react";

interface Props {
  isMobile?: boolean;
}

export function GerenciaSwitcher({ isMobile = false }: Props) {
  const { user } = useAuth();
  const gerenciaActiva = useGerenciaActiva();
  const verTodos = useVerTodasLasGerencias();
  const catalogo = useGerenciasCatalogo();
  const { data: usuarios } = useUsuariosActivos(user?.tenantId ?? "");

  // Catálogo de gerencias ACTIVOS (no desactivados por el ADMIN_PMIS).
  const catalogoActivo = useMemo(
    () => catalogo.filter((p) => p.activo !== false),
    [catalogo],
  );

  const gerenciasPermitidas = useMemo<GerenciaCodigo[]>(() => {
    if (!user) return [];
    const codigosActivos = catalogoActivo.map((p) => p.codigo as GerenciaCodigo);
    // ADMIN_PMIS ve todos los activos por short-circuit.
    if (user.role === "ADMIN_PMIS") {
      return codigosActivos;
    }
    const yo = (usuarios ?? []).find((u) => u.email === user.email);
    const listaUsuario = yo?.gerenciasAccesibles ?? [];
    // Intersección: solo los códigos que el usuario tiene Y que están activos.
    const permitidos = listaUsuario.filter((c) => codigosActivos.includes(c));
    return permitidos.length > 0 ? permitidos : ["CO"];
  }, [user, usuarios, catalogoActivo]);

  // Auto-corrección: si la gerencia persistido NO está en la lista permitida del
  // usuario (cambió de cuenta, los permisos se restringieron), saltamos al
  // primero permitido. Evita queries vacías misteriosas.
  useEffect(() => {
    if (gerenciasPermitidas.length === 0) return;
    if (!gerenciasPermitidas.includes(leerGerenciaActiva())) {
      const fallback = gerenciasPermitidas[0];
      if (fallback) establecerGerenciaActiva(fallback);
    }
  }, [gerenciasPermitidas]);

  if (!user || gerenciasPermitidas.length === 0) return null;

  const info = infoGerencia(gerenciaActiva);
  // Si solo hay UN gerencia permitido, no tiene sentido el dropdown — mostramos
  // el flag + nombre como label estático.
  if (gerenciasPermitidas.length === 1) {
    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: isMobile ? "0 4px" : "0 8px",
          fontSize: isMobile ? 13 : 14,
          color: "white",
          opacity: 0.9,
        }}
        title={`Gerencia del PMIS: ${info.nombre}`}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{info.flag}</span>
        {!isMobile && <span>{info.nombre}</span>}
      </span>
    );
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size={isMobile ? "small" : "medium"}
          icon={<ChevronDownRegular />}
          iconPosition="after"
          style={{
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: isMobile ? 44 : undefined,
            // En modo "ver todos" subrayamos el botón con un acento ámbar
            // para que sea visible que el filtro está desactivado.
            border: verTodos ? "1px solid #F59E0B" : undefined,
            background: verTodos ? "rgba(245, 158, 11, 0.18)" : undefined,
          }}
          title={
            verTodos
              ? "Modo INTER-GERENCIA activo (vista cross-gerencia de solo lectura)"
              : "Cambiar gerencia del PMIS"
          }
        >
          {verTodos ? (
            <>
              <span style={{ fontSize: 18, lineHeight: 1 }}>🌎</span>
              {!isMobile && (
                <span style={{ fontWeight: 600 }}>Todas las gerencias</span>
              )}
            </>
          ) : (
            <>
              <span style={{ fontSize: 18, lineHeight: 1 }}>{info.flag}</span>
              {!isMobile && (
                <span style={{ fontWeight: 600 }}>{info.nombre}</span>
              )}
            </>
          )}
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {gerenciasPermitidas.map((codigo) => {
            const p = infoGerencia(codigo);
            const activo = codigo === gerenciaActiva && !verTodos;
            return (
              <MenuItem
                key={codigo}
                onClick={() => {
                  establecerGerenciaActiva(codigo);
                  // Al elegir una gerencia concreto, salimos del modo "ver todos".
                  if (verTodos) establecerVerTodasLasGerencias(false);
                }}
                style={{
                  fontWeight: activo ? 700 : 400,
                  background: activo ? "#EAF3FB" : undefined,
                }}
              >
                <span style={{ fontSize: 18, marginRight: 8 }}>{p.flag}</span>
                {p.nombre}
                <span
                  style={{
                    fontSize: 10,
                    color: "#6B7280",
                    marginLeft: 8,
                  }}
                >
                  {p.codigo} · {p.monedaLocal}
                </span>
                {activo && (
                  <span
                    style={{
                      marginLeft: "auto",
                      color: "#2A954F",
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                )}
              </MenuItem>
            );
          })}
          <MenuDivider />
          <MenuItem
            onClick={() => establecerVerTodasLasGerencias(!verTodos)}
            style={{
              fontWeight: verTodos ? 700 : 400,
              background: verTodos ? "#FEF3C7" : undefined,
            }}
            title="Modo de consulta inter-gerencia: mostrar datos de todas las gerencias (solo lectura). La edición/creación queda restringida a tu gerencia activa."
          >
            <span style={{ fontSize: 18, marginRight: 8 }}>🌎</span>
            Ver todas las gerencias
            <span style={{ fontSize: 10, color: "#6B7280", marginLeft: 8 }}>
              cross-gerencia (read-only)
            </span>
            {verTodos && (
              <span
                style={{
                  marginLeft: "auto",
                  color: "#92400E",
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
            )}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
